import React, { useState, useRef } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, isWithinInterval, parseISO } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiClock, FiCircle, FiTarget, FiImage, FiLayers } from 'react-icons/fi';
import { TIMELINE_COLORS, WORKFLOW_STAGES } from '../../data/constants';
import { motion, AnimatePresence } from 'framer-motion';

const CalendarView = ({ products }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentMonth)),
    end: endOfWeek(endOfMonth(currentMonth)),
  });

  const getProductTimelinesForDay = (day) => {
    return products.filter(p => {
      if (!p.startDate || !p.targetDate) return false;
      const start = parseISO(p.startDate);
      const end = parseISO(p.targetDate);
      return isWithinInterval(day, { start, end });
    });
  };

  const getFirstImage = (product) => {
    if (!product.checklist) return null;
    // Iterate through checklist to find any image
    for (const key in product.checklist) {
      const data = product.checklist[key];
      if (data?.imageList?.length > 0) return data.imageList[0];
      if (data?.imageUrl) return data.imageUrl;
    }
    return null;
  };

  const handleMouseMove = (e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  return (
    <div className="bg-white rounded-3xl border border-cream-dark shadow-sm overflow-hidden animate-fade-in relative">
      <div className="p-6 border-b border-cream-dark flex items-center justify-between bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-terracotta/10 flex items-center justify-center">
            <SafeIcon icon={FiCalendar} className="text-terracotta w-5 h-5" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-espresso">{format(currentMonth, 'MMMM yyyy')}</h3>
            <p className="text-[10px] text-espresso-light/60 uppercase tracking-widest font-bold">Project Timelines</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 hover:bg-cream rounded-lg text-espresso-light transition-colors"
          >
            <SafeIcon icon={FiChevronLeft} />
          </button>
          <button 
            onClick={() => setCurrentMonth(new Date())}
            className="px-3 py-1 text-xs font-bold text-espresso-light hover:bg-cream rounded-lg border border-cream-dark transition-colors"
          >
            Today
          </button>
          <button 
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2 hover:bg-cream rounded-lg text-espresso-light transition-colors"
          >
            <SafeIcon icon={FiChevronRight} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 bg-cream/30 border-b border-cream-dark">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="py-3 text-center text-[10px] font-bold text-espresso-light/40 uppercase tracking-tighter">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 auto-rows-[minmax(140px,1fr)]">
        {days.map((day, idx) => {
          const activeProducts = getProductTimelinesForDay(day);
          const isCurrentMonth = isSameMonth(day, currentMonth);
          
          return (
            <div 
              key={day.toString()}
              className={`p-2 border-r border-b border-cream-dark min-h-[140px] transition-colors ${!isCurrentMonth ? 'bg-cream/10' : 'bg-white'} ${isToday(day) ? 'bg-terracotta/5' : ''}`}
            >
              <div className="flex justify-between items-start mb-3">
                <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full ${isToday(day) ? 'bg-terracotta text-white' : isCurrentMonth ? 'text-espresso' : 'text-espresso-light/20'}`}>
                  {format(day, 'd')}
                </span>
                {activeProducts.length > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 bg-cream-dark/50 text-espresso-light rounded font-black uppercase tracking-tighter">
                    {activeProducts.length} Active
                  </span>
                )}
              </div>

              <div className="space-y-1.5 relative overflow-hidden">
                {activeProducts.slice(0, 4).map(product => {
                  const isStart = isSameDay(day, parseISO(product.startDate));
                  const isEnd = isSameDay(day, parseISO(product.targetDate));
                  const color = product.timelineColor || TIMELINE_COLORS[0].value;
                  const hasImage = !!getFirstImage(product);
                  
                  return (
                    <div 
                      key={product.id} 
                      className="group relative h-7 flex items-center px-1.5 cursor-pointer"
                      onMouseEnter={() => setHoveredProduct(product)}
                      onMouseLeave={() => setHoveredProduct(null)}
                      onMouseMove={handleMouseMove}
                    >
                      {/* Connection Line */}
                      <div 
                        className="absolute inset-y-[13px] left-0 right-0 h-1 opacity-20" 
                        style={{ backgroundColor: color }}
                      />
                      
                      {/* Visual Content */}
                      <div className="z-10 flex items-center gap-1.5 w-full bg-white/70 backdrop-blur-[2px] rounded-md px-1 py-0.5 border border-transparent group-hover:border-cream-dark group-hover:bg-white shadow-sm transition-all">
                        <div className="flex-shrink-0 flex items-center justify-center">
                          {isStart ? (
                            <div className="w-3 h-3 rounded-full flex items-center justify-center border border-white" style={{ backgroundColor: color }}>
                              <SafeIcon icon={FiCircle} className="w-1.5 h-1.5 text-white/40" />
                            </div>
                          ) : isEnd ? (
                            <div className="w-3.5 h-3.5 rounded bg-white flex items-center justify-center shadow-sm" style={{ color }}>
                              <SafeIcon icon={FiTarget} className="w-3 h-3 fill-current" />
                            </div>
                          ) : (
                            <div className="w-2 h-0.5 rounded-full" style={{ backgroundColor: color }} />
                          )}
                        </div>
                        <span className="text-[9px] font-bold text-espresso truncate leading-tight flex-1">
                          {product.title}
                        </span>
                        {hasImage && (
                          <SafeIcon icon={FiImage} className="w-2.5 h-2.5 text-terracotta opacity-40 group-hover:opacity-100" />
                        )}
                      </div>
                    </div>
                  );
                })}
                {activeProducts.length > 4 && (
                  <div className="text-[8px] text-espresso-light/40 font-black uppercase px-2 pt-1 tracking-widest text-center">
                    + {activeProducts.length - 4} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating Preview Tooltip */}
      <AnimatePresence>
        {hoveredProduct && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="fixed pointer-events-none z-[100] w-64 bg-white rounded-2xl shadow-2xl border border-cream-dark overflow-hidden"
            style={{ 
              left: mousePos.x + 20, 
              top: mousePos.y - 120 
            }}
          >
            <div className="aspect-video bg-cream flex items-center justify-center relative overflow-hidden">
              {getFirstImage(hoveredProduct) ? (
                <img 
                  src={getFirstImage(hoveredProduct)} 
                  alt="" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="flex flex-col items-center gap-2 opacity-20">
                  <SafeIcon icon={FiLayers} className="w-8 h-8" />
                  <span className="text-[9px] font-bold uppercase tracking-widest">No Artwork Yet</span>
                </div>
              )}
              <div 
                className="absolute top-2 left-2 px-2 py-0.5 rounded text-[8px] font-black text-white uppercase tracking-tighter shadow-sm"
                style={{ backgroundColor: hoveredProduct.timelineColor }}
              >
                {hoveredProduct.designNumber}
              </div>
            </div>
            <div className="p-3">
              <h4 className="font-bold text-xs text-espresso mb-1 truncate">{hoveredProduct.title}</h4>
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-bold text-espresso-light/40 uppercase tracking-widest">
                  {hoveredProduct.status}
                </span>
                <div className="flex items-center gap-1">
                  <SafeIcon icon={FiClock} className="w-2.5 h-2.5 text-terracotta" />
                  <span className="text-[9px] font-bold text-espresso-light">
                    Due {new Date(hoveredProduct.targetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CalendarView;